import { Cable, X, Ship, Info } from "lucide-react";
import { categoryLabel } from "@/lib/maritime";

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-1 border-b border-slate-800/40 last:border-0">
    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500 shrink-0">{label}</span>
    <span className="text-sm text-slate-200 text-right">{value ?? "—"}</span>
  </div>
);

export const CablePanel = ({ cable, vessels = [], radiusKm = 10, onClose, onSelectVessel }) => {
  if (!cable) return null;
  const p = cable;
  return (
    <aside
      data-testid="cable-detail-panel"
      className="absolute top-20 right-4 z-30 w-80 max-h-[calc(100vh-8rem)] flex flex-col rounded-xl bg-slate-900/97 backdrop-blur-xl border border-[#6366F1]/30 shadow-2xl overflow-hidden st-fade"
    >
      <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-slate-800/80">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Cable className="h-4 w-4 text-[#818CF8]" />
            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#6366F1]/15 text-[#A5B4FC]">
              Undersea Cable
            </span>
          </div>
          <h2 className="font-display text-lg font-bold text-white leading-tight">{p.short_name}</h2>
          <p className="text-[11px] text-slate-400">{p.name}</p>
        </div>
        <button data-testid="cable-close" onClick={onClose} className="text-slate-500 hover:text-slate-200">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto st-scroll px-4 py-3 space-y-3">
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
          <Row label="Status" value={p.status} />
          <Row label="Ready for service" value={p.rfs_year} />
          <Row label="Capacity" value={p.capacity_tbps != null ? `${p.capacity_tbps} Tbps` : "Not published"} />
          <Row label="Type" value="International submarine fibre-optic cable" />
        </div>

        <div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Landing countries</span>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {(p.landing_countries || []).map((c) => (
              <span key={c} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-300 border border-slate-700/50">
                {c}
              </span>
            ))}
          </div>
        </div>

        <div data-testid="cable-nearby-vessels">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#A5B4FC]">
              Vessels within {radiusKm} km
            </span>
            <span className="font-mono text-[11px] text-slate-500">{vessels.length}</span>
          </div>
          {vessels.length === 0 && (
            <p data-testid="cable-no-vessels" className="text-center text-xs text-slate-500 py-4 font-mono">
              No AIS vessels currently within {radiusKm} km.
            </p>
          )}
          <div className="space-y-1.5">
            {vessels.slice(0, 60).map((v) => (
              <button
                key={v.mmsi}
                data-testid={`cable-vessel-${v.mmsi}`}
                onClick={() => onSelectVessel?.(v.mmsi)}
                className="w-full text-left rounded-lg border border-slate-800/70 bg-slate-800/40 px-3 py-2 hover:bg-slate-800/70 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Ship className="h-3.5 w-3.5 text-[#818CF8] shrink-0" />
                  <span className="text-sm text-slate-200 font-medium truncate flex-1">
                    {(v.name || "").trim() || `MMSI ${v.mmsi}`}
                  </span>
                  <span className="font-mono text-[10px] text-[#A5B4FC] shrink-0">{v.distance_km} km</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5 pl-5.5 flex-wrap">
                  <span className="font-mono text-[10px] text-slate-500">{categoryLabel(v.category)}</span>
                  <span className="font-mono text-[10px] text-slate-600">·</span>
                  <span className="font-mono text-[10px] text-slate-500">{v.flag?.iso2 || "—"}</span>
                  <span className="font-mono text-[10px] text-slate-600">·</span>
                  <span className="font-mono text-[10px] text-slate-500">{v.sog != null ? `${v.sog} kn` : "—"}</span>
                  {v.heading != null && <span className="font-mono text-[10px] text-slate-500">· {v.heading}°</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="pt-1 space-y-1.5 border-t border-slate-800/50">
          <Row label="Source" value={p.source} />
          <div className="flex items-start gap-1.5 pt-1">
            <Info className="h-3 w-3 text-slate-600 mt-0.5 shrink-0" />
            <p className="text-[10px] leading-relaxed text-slate-500">
              Cable routes are indicative and derived from publicly available information. They should not be
              relied upon for navigation, engineering or subsea operations.
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default CablePanel;
